import styles from './styles.module.scss';

import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Board } from '../../../../../../components/Board';
import { NavButton } from '../../../../../../components/NavButton';
import { ProfileBox } from '../../../../../../components/ProfileBox';
import { SelectionList } from '../../../../../../components/SelectionList';
import { SelectionItem } from '../../../../../../components/SelectionListItem';
import { rolesToString, verifyRoutePermissions } from '../../../../../../globalFunctions';
import { useUser } from '../../../../../../hooks/useUser';
import { MessageContext } from '../../../../../../providers/MessageProvider';
import { User, LoadData, UserOptions, AcademicUser } from '../../../../../../types';
import { LogicManagerUserDetail } from '../LogicManagerUserDetail';
import { useOptions } from '../../../../../../hooks/useOptions';
import { LoadingContext } from '../../../../../../providers/LoadingProvider';
import { isEqual } from 'lodash';
import { UserDAO } from '../../../../../../classes/UserDAO';
import { AlertDialog, AlertDialogType } from '../../../../../../components/AlertDialog';
import { MediaContentDAO } from '../../../../../../classes/MediaContentDAO';

export const LogicManagerUserMaintainRoute = () => {
    const [userAuth, setUserAuth] = useState<User>({} as User);
    const navigate = useNavigate();

    // Load data
    const options: LoadData = useOptions('user');
    const users: LoadData = useUser();
    const [userOptions, setUserOptions] = useState<UserOptions>();
    const [usersList, setUsersList] = useState<SelectionItem[]>();
    const [loadedUsers, setLoadedUsers] = useState<AcademicUser[]>();
    const [selectedUser, setSelectedUser] = useState<AcademicUser>();

    // Handle changes
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [alertDialogProps, setAlertDialogProps] = useState<AlertDialogType>();
    const [originalData, setOriginalData] = useState<AcademicUser>();
    const { setIsLoadingContent } = useContext(LoadingContext);
    const { setOpenMessage, setMessage } = useContext(MessageContext);

    const save = async () => {
        if (selectedUser) {
            try {
                setIsLoadingContent(true);

                let mediaContent: any = undefined;

                if (selectedUser.image?.file) {
                    mediaContent = await MediaContentDAO.store(
                        userAuth.token,
                        navigate,
                        selectedUser.id,
                        selectedUser.image,
                        'user'
                    );

                    if (
                        mediaContent &&
                        typeof mediaContent === 'object' &&
                        Object.keys(mediaContent).includes('error')
                    ) {
                        throw mediaContent as Error;
                    }
                }

                const result = await UserDAO.save(userAuth.token, navigate, selectedUser);

                if (
                    result &&
                    typeof result === 'object' &&
                    !Object.keys(result).includes('error')
                ) {
                    const currentUser = Object.assign({}, selectedUser);
                    currentUser.schools = result.user.schools;
                    currentUser.image = mediaContent ? mediaContent.image : currentUser.image;

                    setSelectedUser({ ...currentUser });
                    setOriginalData({ ...currentUser });
                    updateUsersList({ ...currentUser });
                    updateLoadedUsers({ ...currentUser });

                    setMessage({
                        type: 'success',
                        content: 'Usuário salvo com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const restore = async () => {
        if (selectedUser) {
            try {
                setIsLoadingContent(true);

                const result = await UserDAO.restore(userAuth.token, navigate, selectedUser.id);

                if (result) {
                    const currentUser = Object.assign({}, originalData);
                    currentUser.is_active = true;

                    setSelectedUser({ ...currentUser });
                    setOriginalData({ ...currentUser });
                    updateUsersList({ ...currentUser });
                    updateLoadedUsers({ ...currentUser });

                    setMessage({
                        type: 'success',
                        content: 'Usuário reativado com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const remove = async () => {
        if (selectedUser) {
            try {
                setIsLoadingContent(true);

                const result = await UserDAO.delete(userAuth.token, navigate, selectedUser.id);

                if (result) {
                    const currentUser = Object.assign({}, originalData);
                    currentUser.is_active = false;

                    setSelectedUser({ ...currentUser });
                    setOriginalData({ ...currentUser });
                    updateUsersList({ ...currentUser });
                    updateLoadedUsers({ ...currentUser });

                    setMessage({
                        type: 'success',
                        content: 'Usuário desativado com sucesso'
                    });
                    setOpenMessage(true);
                } else {
                    throw result as Error;
                }
            } catch (error: any) {
                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            } finally {
                setIsLoadingContent(false);
            }
        }
    };

    const reset = () => {
        if (originalData) {
            setSelectedUser({ ...originalData });
        }
    };

    const validate = (user: AcademicUser) => {
        if (user.name.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo nome'
            });
            setOpenMessage(true);

            return false;
        }

        if (user.surname.trim() === '') {
            setMessage({
                type: 'warning',
                content: 'Preencha o campo sobrenome'
            });
            setOpenMessage(true);

            return false;
        }

        if (user.roles.length === 0) {
            setMessage({
                type: 'warning',
                content: 'Selecione pelo menos um papel para o usuário'
            });
            setOpenMessage(true);

            return false;
        } else {
            if (user.roles.find(role => ['ACADEMIC'].includes(role.internal_type))) {
                if (user.roles.find(role => ['DEFAULT'].includes(role.internal_type))) {
                    setMessage({
                        type: 'warning',
                        content: 'Este usuário é acadêmico, remova o perfil de acesso "Usuário".'
                    });
                    setOpenMessage(true);

                    return false;
                }
            }
        }

        if (user.schools.length > 0) {
            let hasEmptyAcademicRoles = false;

            user.schools.forEach(school => {
                if (school.idAcademicRole === 0) {
                    hasEmptyAcademicRoles = true;
                }
            });

            if (hasEmptyAcademicRoles) {
                setMessage({
                    type: 'warning',
                    content: 'Selecione a função acadêmica do usuário nas instituições'
                });
                setOpenMessage(true);

                return false;
            }
        }

        return true;
    };

    const onSave = async () => {
        try {
            setIsLoadingContent(true);

            if (!isEqual(selectedUser, originalData) && selectedUser) {
                if (validate(selectedUser)) {
                    await save();
                }
            } else {
                setMessage({
                    type: 'info',
                    content: 'Sem alterações para salvar'
                });
                setOpenMessage(true);
            }
        } catch (error: any) {
            const e = error as Error;

            setMessage({
                type: 'error',
                content: e.message
            });
            setOpenMessage(true);
        } finally {
            setIsLoadingContent(false);
        }
    };

    const onRestore = () => {
        setAlertDialogProps({
            title: 'Confirmar ativação',
            message: 'Deseja reativar este usuário?',
            options: [
                {
                    text: 'Reativar',
                    action: async () => await restore(),
                    type: 'warning'
                }
            ]
        });
        setOpenAlert(true);
    };

    const onDelete = () => {
        setAlertDialogProps({
            title: 'Confirmar desativação',
            message: 'Deseja realmente desativar este usuário?',
            options: [
                {
                    text: 'Desativar',
                    action: async () => await remove(),
                    type: 'error'
                }
            ]
        });
        setOpenAlert(true);
    };

    const onSelectUser = (id: any) => {
        return new Promise<boolean>((resolve, reject) => {
            if (loadedUsers) {
                loadedUsers.forEach((user: AcademicUser) => {
                    if (user.id === id) {
                        if (
                            selectedUser &&
                            originalData &&
                            user.id !== originalData.id &&
                            !isEqual(selectedUser, originalData)
                        ) {
                            setAlertDialogProps({
                                title: 'Confirmar alterações',
                                message: 'Deseja salvar as alterações?',
                                options: [
                                    {
                                        text: 'Desfazer',
                                        action: reset,
                                        type: 'error'
                                    },
                                    {
                                        text: 'Salvar',
                                        action: async () => await save(),
                                        type: 'success'
                                    }
                                ]
                            });
                            setOpenAlert(true);

                            resolve(false);
                        } else {
                            const currentSelectedUser = Object.assign({}, user);
                            setSelectedUser({ ...currentSelectedUser });
                            setOriginalData({ ...currentSelectedUser });

                            resolve(true);
                        }
                    }
                });
            }

            reject(false);
        });
    };

    const goBackwards = () => {
        if (isEqual(selectedUser, originalData)) {
            navigate(`/manager`);
        } else {
            setAlertDialogProps({
                title: 'Confirmar alterações',
                message: 'Deseja salvar as alterações?',
                options: [
                    {
                        text: 'Desfazer',
                        action: reset,
                        type: 'error'
                    },
                    {
                        text: 'Salvar',
                        action: async () => await save(),
                        type: 'success'
                    }
                ]
            });
            setOpenAlert(true);
        }
    };

    const updateUsersList = (currentUser: AcademicUser) => {
        if (usersList) {
            const usersData: SelectionItem[] = [];

            usersList.forEach((user: SelectionItem) => {
                if (user.id === currentUser.id) {
                    usersData.push({
                        id: currentUser.id,
                        name: `${currentUser.name} ${currentUser.surname}`,
                        image: currentUser.image?.url,
                        description: currentUser.is_yours
                            ? 'Você'
                            : rolesToString(currentUser.roles, '/')
                    });
                } else {
                    usersData.push(user);
                }
            });

            setUsersList(usersData);
        }
    };

    const updateLoadedUsers = (currentUser: AcademicUser) => {
        if (loadedUsers) {
            const usersData: AcademicUser[] = [];

            loadedUsers.forEach((user: AcademicUser) => {
                if (user.id === currentUser.id) {
                    usersData.push(currentUser);
                } else {
                    usersData.push(user);
                }
            });

            setLoadedUsers(usersData);
        }
    };

    useEffect(() => {
        if (!users.isLoading) {
            const data = users.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                const usersData: SelectionItem[] = [];

                users.data.forEach((user: AcademicUser) => {
                    usersData.push({
                        id: user.id,
                        name: `${user.name} ${user.surname}`,
                        image: user.image?.url,
                        description: user.is_yours ? 'Você' : rolesToString(user.roles, '/')
                    });
                });

                setUsersList(usersData);
                setLoadedUsers(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [users.isLoading]);

    useEffect(() => {
        if (!options.isLoading) {
            const data = options.data;

            if (data && typeof data === 'object' && !Object.keys(data).includes('error')) {
                setUserOptions(data);
            } else {
                const error = data as Error;

                setMessage({
                    type: 'error',
                    content: error.message
                });
                setOpenMessage(true);
            }
        }
    }, [options.isLoading]);

    useEffect(() => {
        const user = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.admin'],
            navigate
        );

        if (typeof user === 'object') {
            setUserAuth(user);
        }
    }, []);

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '1vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />
            {userAuth.token && usersList && userOptions && (
                <>
                    <ProfileBox user={userAuth} />
                    <Board wrapperClass={styles.board}>
                        <div className={styles.boardWrapper}>
                            <h1 className={styles.title}>Usuários</h1>

                            <div className={styles.editContainer}>
                                <SelectionList
                                    items={usersList}
                                    defaultSelectedItem={
                                        selectedUser &&
                                        ({
                                            id: selectedUser.id,
                                            name: `${selectedUser.name} ${selectedUser.surname}`,
                                            image: selectedUser.image && selectedUser.image.url,
                                            description: selectedUser.is_yours
                                                ? 'Você'
                                                : rolesToString(selectedUser.roles, '/')
                                        } as SelectionItem)
                                    }
                                    onSelectItem={onSelectUser}
                                    searchPlaceholder='Pesquisar'
                                    wrapperClass={styles.list}
                                />
                                <LogicManagerUserDetail
                                    user={selectedUser}
                                    setUser={setSelectedUser}
                                    options={userOptions}
                                    onSave={onSave}
                                    onRestore={onRestore}
                                    onDelete={onDelete}
                                />
                            </div>
                        </div>
                    </Board>
                </>
            )}
            {alertDialogProps && (
                <AlertDialog
                    setOpen={setOpenAlert}
                    open={openAlert}
                    title={alertDialogProps.title}
                    message={alertDialogProps.message}
                    options={alertDialogProps.options}
                />
            )}
        </div>
    );
};
